/**
 * First-Party Cookie Management
 * 
 * Manages first-party cookies for Meta (Facebook) and Google ad tracking.
 * These cookies enable proper attribution when users arrive from paid ads
 * and help maintain tracking across iOS/Safari restrictions.
 */

// Cookie configuration
const COOKIE_EXPIRY_DAYS = 90;
const SECURE_COOKIE_OPTIONS = 'path=/; SameSite=Lax; Secure';

/**
 * Set a first-party cookie with proper security attributes
 */
export const setFirstPartyCookie = (name: string, value: string, days: number = COOKIE_EXPIRY_DAYS): void => {
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; ${SECURE_COOKIE_OPTIONS}`;
};

/**
 * Get a cookie value by name
 */
export const getCookie = (name: string): string | null => {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : null;
};

/**
 * Delete a cookie by name
 */
export const deleteCookie = (name: string): void => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

/**
 * Capture Facebook Click ID (fbclid) from URL and store as _fbc cookie
 * Format: fb.1.{creation_time}.{fbclid}
 */
export const captureFbclid = (): string | null => {
  try {
    const params = new URLSearchParams(window.location.search);
    const fbclid = params.get('fbclid');
    
    if (fbclid) {
      // Meta's _fbc format: fb.{subdomain_index}.{creation_time}.{fbclid}
      const fbc = `fb.1.${Date.now()}.${fbclid}`;
      setFirstPartyCookie('_fbc', fbc);
      console.debug('[FirstPartyCookies] Captured fbclid:', fbc.substring(0, 30) + '...');
      return fbc;
    }
    
    // Return existing cookie if no new fbclid in URL
    return getCookie('_fbc');
  } catch (error) {
    console.debug('[FirstPartyCookies] Error capturing fbclid:', error);
    return null;
  }
};

/**
 * Capture Google Click ID (gclid) from URL and store as _gcl_aw cookie
 */
export const captureGclid = (): string | null => {
  try {
    const params = new URLSearchParams(window.location.search);
    const gclid = params.get('gclid');
    
    if (gclid) {
      // Google's format includes timestamp
      const gclAw = `GCL.${Date.now()}.${gclid}`;
      setFirstPartyCookie('_gcl_aw', gclAw);
      console.debug('[FirstPartyCookies] Captured gclid:', gclAw.substring(0, 30) + '...');
      return gclid;
    }
    
    return getCookie('_gcl_aw');
  } catch (error) {
    console.debug('[FirstPartyCookies] Error capturing gclid:', error);
    return null;
  }
};

/**
 * Get Facebook Browser ID (_fbp cookie)
 * This is set by the Meta Pixel automatically, we just need to read it
 */
export const getFbp = (): string | null => {
  return getCookie('_fbp');
};

/**
 * Get Facebook Click ID (_fbc cookie)
 */
export const getFbc = (): string | null => {
  return getCookie('_fbc');
};

/**
 * Get Google Click ID
 */
export const getGclid = (): string | null => {
  const gclAw = getCookie('_gcl_aw');
  if (gclAw) {
    // Extract just the gclid from the formatted string
    const parts = gclAw.split('.');
    return parts.length > 2 ? parts.slice(2).join('.') : gclAw;
  }
  return null;
};

/**
 * Capture all marketing parameters from URL on page load
 * Call this once when the page loads
 */
export const captureMarketingParams = (): {
  fbc: string | null;
  fbp: string | null;
  gclid: string | null;
} => {
  const fbc = captureFbclid();
  const gclid = captureGclid();
  const fbp = getFbp();
  
  return { fbc, fbp, gclid };
};

/**
 * Generate a unique event ID for deduplication
 * Used to match client-side and server-side events
 */
export const generateEventId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Get all tracking identifiers for server-side tracking
 */
export const getTrackingIdentifiers = (): {
  fbc: string | null;
  fbp: string | null;
  gclid: string | null;
  visitorId: string;
  sessionId: string;
} => {
  return {
    fbc: getFbc(),
    fbp: getFbp(),
    gclid: getGclid(),
    visitorId: localStorage.getItem('obc_visitor_id') || '',
    sessionId: sessionStorage.getItem('obc_session_id') || '',
  };
};
